import { useState } from "react";
import {
  useSurgeries,
  useCreatePatientSurgery,
  useDeletePatientSurgery,
} from "@/hooks/useConsultation";
import { useToast } from "@/hooks/use-toast";
import { SearchableChipDialog } from "@/components/consultation/shared/SearchableChipDialog";
import type {
  CreatePatientSurgeryRequest,
  PatientSurgery,
} from "@/types/appointments";

interface AddSurgeryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  appointmentId: string;
  patientSurgeries?: PatientSurgery[];
}

export const AddSurgeryDialog = ({
  open,
  onOpenChange,
  appointmentId,
  patientSurgeries = [],
}: AddSurgeryDialogProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const { toast } = useToast();

  const { data: surgeries = [] } = useSurgeries();
  const createMutation = useCreatePatientSurgery();
  const deleteMutation = useDeletePatientSurgery();

  const handleAddClick = async (surgeryId: string) => {
    try {
      const requestData: CreatePatientSurgeryRequest = {
        surgery: Number(surgeryId),
      };

      await createMutation.mutateAsync({
        appointmentId,
        data: requestData,
      });
      toast({
        title: "Success",
        description: "Surgical history added successfully",
      });
    } catch {
      toast({
        title: "Error",
        description: "Failed to add surgical history",
        variant: "destructive",
      });
    }
  };

  const handleRemoveClick = async (id: string) => {
    const entry = patientSurgeries.find((s) => s.id === id);
    if (!entry) return;
    try {
      await deleteMutation.mutateAsync({
        appointmentId,
        surgeryId: entry.id,
      });
      toast({
        title: "Success",
        description: "Surgical history removed successfully",
      });
    } catch {
      toast({
        title: "Error",
        description: "Failed to remove surgical history",
        variant: "destructive",
      });
    }
  };

  // Filter available surgeries
  const addedSurgeryIds = new Set(
    patientSurgeries.map((s) => s.surgery_details.id)
  );

  const availableSurgeries = surgeries.filter((surgery) => {
    const matchesSearch =
      surgery.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      surgery.snomed_ct_code?.toLowerCase().includes(searchTerm.toLowerCase());
    const notAdded = !addedSurgeryIds.has(surgery.id);
    return matchesSearch && notAdded;
  });

  return (
    <SearchableChipDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Add Surgical History"
      addedItems={patientSurgeries.map((s) => ({
        id: s.id,
        label: s.surgery_details.name,
      }))}
      onRemoveItem={handleRemoveClick}
      searchLabel="Add surgical history here"
      searchPlaceholder="Search Here"
      searchResults={availableSurgeries.map((s) => ({
        id: String(s.id),
        label: s.name,
      }))}
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      minSearchChars={2}
      emptySearchMessage="No matching surgeries found"
      onAddItem={handleAddClick}
    />
  );
};
